"use client";

import { useEffect, useMemo, useState } from "react";
import { useRouter, useParams } from "next/navigation";
import { FilteredFacet, SortItem } from "@/apiUtils/algoliaUtils/types";
import FilterIcon from "@/assets/icons/filter.svg?react";
import { PopularBrandItem, Range, TranslationData } from "@/common/lib/types";
import { getLangFromLocale } from "@/utils/common";
import {
  extractSelectedSearchParams,
  getUpdatedSearchParams,
  removerSearchValue,
} from "@/utils/filter.utils";
import BottomSheet from "../ui/bottomSheet";
import Dropdown from "../ui/dropdown";
import AllFilters from "./allFilters/AllFilters";
import TabContents from "./allFilters/TabContents";
import SortList from "./itemLists/SortList";
import SelectedFilters from "./selectedFilters";
import { getAlgoliaIndexName } from "@/apiUtils/algoliaUtils";
import { algoliaFilterEventClient } from "@/apiUtils/algoliaUtils/indexClient";
import { algoliaEventParam, eventParam } from "@/apiUtils/algoliaUtils/constants";
import { useGlobalStore } from "@/store/store";

interface IProps {
  locale: string;
  totalProducts: number;
  sortFilters: SortItem[];
  filters: FilteredFacet[];
  searchParams?: Record<string, string>;
  priceRange?: Range;
  popularBrands: { title: string; brands: PopularBrandItem[] };
  translations: TranslationData;
}

const Filter = ({
  locale,
  totalProducts,
  sortFilters,
  filters,
  searchParams = {},
  priceRange,
  popularBrands,
  translations,
}: IProps) => {
  const router = useRouter();
  const params = useParams();
  const [openAllFilters, setOpenAllFilters] = useState(false);
  const [openSort, setOpenSort] = useState(false);
  const [currentSearchParams, setCurrentSearchParams] = useState<Record<string, string>>({});
  const { user } = useGlobalStore((state) => state);

  const lang = getLangFromLocale(params.locale as string);

  const appliedFilters = useMemo(
    () => extractSelectedSearchParams(searchParams, locale, filters),
    [searchParams, locale, filters],
  );
  const selectedSort = useMemo(() => {
    const selected = sortFilters?.find((item) => item?.value === currentSearchParams?.sort);
    return selected?.label || sortFilters[0]?.label;
  }, [sortFilters, currentSearchParams?.sort]);

  const SKIP_COUNT_KEYS = useMemo(
    () => new Set(["page", "sort", "pmax", "_p-l", "q"]),
    [],
  );
  const appliedCount = useMemo(
    () =>
      Object.entries(appliedFilters).reduce((acc, [key, value]) => {
        if (SKIP_COUNT_KEYS.has(key)) return acc;
        if (key === "pmin") return acc + 1;
        return acc + value.split("|").length;
      }, 0),
    [appliedFilters],
  );

  const exposedFilters = filters?.slice(0, 3);
  //?.filter(
  //   (f) => f.type === "frameColor" || f?.type === "material" || f?.type === "price",
  // );
  const handleOpenAllFilter = () => {
    setOpenAllFilters(true);
    document.getElementById("filter-section")?.classList.add("z-30");
  };

  const handleCloseAllFilter = () => {
    setOpenAllFilters(false);
    document.getElementById("filter-section")?.classList.remove("z-30");
  };

  const handleOpenSort = () => {
    setOpenSort(true);
  };

  const handleCloseSort = () => {
    setOpenSort(false);
  };

  const handleChangeSearchParams = (key: string, value: string) => {
    setCurrentSearchParams((prevParams) => {
      const newParams = getUpdatedSearchParams(prevParams, key, value);

      try {
        const existingValues = prevParams?.[key]?.split("|") || [];
        const isAdding = !existingValues.includes(value) && value !== "";

        if (isAdding) {
          const indexName = getAlgoliaIndexName("", locale);
          let filterString = "";

          if (key === "pmin" || key === "pmax") {
            const pmin = key === "pmin" ? value : prevParams?.pmin || "0";
            const pmax = key === "pmax" ? value : prevParams?.pmax || "0";
            if (pmin || pmax) {
              filterString = `price:${pmin}-${pmax}`;
            }
          } else if (key !== "sort") {
            filterString = `${key}:${value}`;
          }

          if (filterString) {
            algoliaFilterEventClient(
              algoliaEventParam.CLICKED_FILTERS,
              indexName,
              eventParam.FILTER_CLICK,
              [filterString],
              user?.magrabi_id,
            );
          }
        }
      } catch (error) {
        console.error("Algolia filter event error:", error);
      }

      return newParams;
    });
    setOpenSort(false);
  };

  const handleRemoveSearchValue = (key: string, value: string, all?: boolean) => {
    if (all) return setCurrentSearchParams({});
    setCurrentSearchParams((prevParams) => removerSearchValue(prevParams, key, value));
  };

  useEffect(() => {
    setCurrentSearchParams(searchParams);
  }, []);

  useEffect(() => {
    // update currentSearchParams when page number changes
    if (searchParams && searchParams.page && searchParams.page !== currentSearchParams?.page)
      setCurrentSearchParams((prev) => ({ ...prev, page: searchParams.page }));
  }, [searchParams]);

  useEffect(() => {
    const params = new URLSearchParams(currentSearchParams);
    router.replace(`?${params.toString()}`, { scroll: false });
  }, [currentSearchParams]);

  return (
    <>
      <div className="hidden items-center justify-between gap-2.5 border-b-[1px] lg:flex">
        <div className="flex items-center gap-3">
          {filters?.length > 0 && (
            <button
              className={`inline-flex items-center px-2 py-5 text-sm font-medium uppercase hover:bg-tertiary-3`}
              onClick={handleOpenAllFilter}
            >
              <FilterIcon className="me-2" />
              {translations?.plp?.plp_all_filters_label || ""}
            </button>
          )}
          {exposedFilters?.map((filter, index) => {
            if (!filter.facetMapping) return <></>;
            return (
              <Dropdown key={index} title={filter.name[lang]}>
                <TabContents
                  exposed
                  filter={filter}
                  selectedTab={filter.type}
                  priceRange={priceRange}
                  searchParams={currentSearchParams}
                  translations={translations}
                  popularBrands={popularBrands}
                  lang={lang}
                  onChange={handleChangeSearchParams}
                />
              </Dropdown>
            );
          })}
          {sortFilters?.length > 0 && (
            <Dropdown title={`${translations?.plp?.plp_sort_label}: ${selectedSort}`}>
              <SortList
                sortOptions={sortFilters}
                selected={currentSearchParams?.sort || "popular"}
                onChange={handleChangeSearchParams}
              />
            </Dropdown>
          )}
        </div>

        <p className="text-sm">
          {translations?.plp?.plp_product_count_multiple_label.replace(
            "{{count}}",
            `${totalProducts}`,
          )}
        </p>
      </div>
      <SelectedFilters
        appliedFilters={appliedFilters}
        filters={filters}
        lang={lang}
        clearAll
        translations={translations}
        onRemove={handleRemoveSearchValue}
        className="hidden bg-transparent lg:flex"
      />

      <div className="flex h-[47px] items-center justify-between bg-tertiary-3 py-4 lg:hidden">
        <div className="flex flex-1 items-center justify-center">
          {filters?.length > 0 && (
            <button className="text-[13px] font-semibold uppercase" onClick={handleOpenAllFilter}>
              {translations?.plp?.plp_all_filters_label || ""}{" "}
              {appliedCount > 0 ? `(${appliedCount})` : ""}
            </button>
          )}
        </div>

        <div className="h-4 w-[1px] bg-tertiary-4" />

        <div className="flex flex-1 items-center justify-center">
          {sortFilters?.length > 0 && (
            <>
              <button
                className="flex items-center text-[13px] font-semibold uppercase"
                onClick={handleOpenSort}
              >
                {translations?.plp?.plp_sort_label}
                <svg
                  className="ml-2 h-4 w-4 transition-transform duration-200"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M19 9l-7 7-7-7"
                  />
                </svg>
              </button>
              <BottomSheet open={openSort} onClose={handleCloseSort}>
                <SortList
                  sortOptions={sortFilters}
                  selected={currentSearchParams?.sort || "popular"}
                  onChange={handleChangeSearchParams}
                />
              </BottomSheet>
            </>
          )}
        </div>
      </div>
      <AllFilters
        locale={locale}
        lang={lang}
        filters={filters}
        open={openAllFilters}
        priceRange={priceRange}
        searchParams={currentSearchParams}
        totalProductCount={totalProducts}
        translations={translations}
        popularBrands={popularBrands}
        onClose={handleCloseAllFilter}
        onChange={handleChangeSearchParams}
        onRemove={handleRemoveSearchValue}
      />
    </>
  );
};

export default Filter;
